// Mi espacio › Hoy · panel de seguimiento (pure, tested by scripts/test-followup-panel.mjs).
// One place to look at a contact, register what happened and leave the next step. The records stay separate:
// the interaction is an Outreach record (the commercial activity, as before), the contact's state is
// Stakeholders › Status, and the next action is a Personal Task of Mi espacio linked to the contact by id
// (Source { kind: 'contact', stakeholderId, accountId, op }). Opening the panel, opening a channel or writing a
// note never completes a task: a task is done or rescheduled only by its explicit button.
import { CONFIRMED_MARK } from './outreachEvidence.js';
import { INSTANT_MARK } from './activityDate.js';
import { PROPOSAL_REQUEST_MARK } from './proposalRequest.js';
import { addDaysIso } from './coachingTime.js';

/** What happened, as Ale registers it. `status` goes to the activity; `contact` is the contact's state it implies. */
export const OUTCOMES = [
  { key: 'none', label: 'Solo consulté (no registra nada)' },
  { key: 'contacted', label: 'Le escribí o lo llamé', status: 'Sent', contact: 'Contacted', advance: true },
  { key: 'replied', label: 'Respondió', status: 'Replied', contact: 'Replied' },
  // An answer asking for a proposal: the activity carries the structured mark (validated by airtable-proxy)
  { key: 'proposal', label: 'Pidió una propuesta', status: 'Replied', contact: 'Replied', request: true },
  { key: 'meeting', label: 'Reunión agendada', status: 'Meeting Scheduled', contact: 'Meeting Booked' },
  { key: 'notinterested', label: 'No le interesa', status: 'Not Interested', contact: 'Not Interested' },
  { key: 'bounced', label: 'Rebotó el correo', status: 'Bounced', contact: 'Bounced' },
];
export const CHANNELS = [['Email', 'Correo'], ['WhatsApp', 'WhatsApp'], ['LinkedIn', 'LinkedIn'], ['Phone', 'Llamada'], ['Meeting', 'En persona']];
const PRIORITY = { '': 0, 'Not Contacted': 0, 'Contacted': 1, 'Replied': 2, 'Meeting Booked': 3 };
const PROTECTED = ['DNC', 'Left Company', 'Not Interested', 'Nurture', 'Bounced'];

/**
 * The contact's state after an outcome, or null when it does not change. It reflects what happened: a reply, a
 * meeting, «no le interesa» or a bounce are set as registered (never a meeting downgraded to a reply); a contact made
 * only advances an earlier state and never overrides DNC, Left Company, Not Interested, Nurture or Bounced.
 * «Sin seguimiento por ahora» is not an outcome: it never changes the state.
 */
export function contactStatusAfter(outcomeKey, current = '') {
  const o = OUTCOMES.find(x => x.key === outcomeKey);
  const cur = String(current || '').trim();
  if (!o?.contact) return null;
  if (o.advance) return !PROTECTED.includes(cur) && (PRIORITY[o.contact] ?? 0) > (PRIORITY[cur] ?? 0) ? o.contact : null;
  if (o.contact === 'Replied' && cur === 'Meeting Booked') return null;
  return o.contact === cur ? null : o.contact;
}

export const opMark = (op) => `[op:${op}]`;
const OP_RE = /^[A-Za-z0-9-]{8,64}$/;
export const validOp = (op) => OP_RE.test(String(op || ''));

/** The activity of an outcome (Outreach fields). A contact made is marked as confirmed by Ale; every record carries its op mark. */
export function activityFields({ outcome, channel = 'Email', note = '', stakeholderId, accountIds = [], name = '', op, loggedBy = '', at = new Date().toISOString(), meetingAt = null }) {
  const o = OUTCOMES.find(x => x.key === outcome);
  if (!o?.status || !validOp(op)) return null;
  const text = String(note || '').trim().slice(0, 2000);
  // INSTANT_MARK: Date is the moment Ale registered it (or the meeting time), a real instant (src/activityDate.js)
  const notes = outcome === 'contacted'
    ? `${CONFIRMED_MARK} ${opMark(op)} ${INSTANT_MARK} Contacto confirmado desde el panel de seguimiento${text ? ` · ${text}` : ''}`
    : o.request
      ? `${PROPOSAL_REQUEST_MARK} ${opMark(op)} ${INSTANT_MARK} ${text || 'Pidió una propuesta (registrado desde el panel de seguimiento)'}`
      : `${opMark(op)} ${INSTANT_MARK} ${text || 'Registrado desde el panel de seguimiento'}`;
  return {
    // No date in the title: the date shown is the Date field's (an instant, src/activityDate.js)
    'Activity Name': `${o.label} — ${name}`.slice(0, 200),
    'Account': accountIds, 'Stakeholder': [stakeholderId],
    'Channel': outcome === 'meeting' ? 'Calendar' : outcome === 'bounced' ? 'Email' : channel,
    'Date': outcome === 'meeting' && meetingAt ? meetingAt : at,
    'Status': o.status, 'Notes': notes, 'Logged By': loggedBy,
  };
}

/** Date shortcuts in Ale's time zone: `today` is her local day (YYYY-MM-DD). */
export const DUE_SHORTCUTS = [['tomorrow', 'Mañana', 1], ['3days', 'En 3 días', 3], ['week', 'En 1 semana', 7]];
export const dueFromShortcut = (key, today) => { const s = DUE_SHORTCUTS.find(x => x[0] === key); return s && /^\d{4}-\d{2}-\d{2}$/.test(today || '') ? addDaysIso(today, s[2]) : null; };

/** A suggested next action for an outcome (Ale edits it). */
export const suggestedNext = (outcome, name) => ({
  contacted: `Seguimiento a ${name} si no responde`, replied: `Responder a ${name}`, proposal: `Preparar y enviar la propuesta a ${name}`, meeting: `Preparar la reunión con ${name}`,
  notinterested: '', bounced: `Buscar otro contacto de ${name}`, none: `Seguimiento a ${name}`,
}[outcome] ?? `Seguimiento a ${name}`);

/**
 * Whether the close of the panel is complete. An interaction (an outcome, or an email sent from the panel) needs a
 * next step: a new action with a date, keeping or rescheduling the one already planned, or «Sin seguimiento por
 * ahora». A new action is allowed only when no other open action of this contact stays open (the ones Ale marks done
 * in this save do not count), so two open actions are never created for one contact.
 *   next: { mode: 'new' | 'keep' | 'reschedule' | 'none' | '', title, dueOn }
 */
export function closeProblem({ outcome = 'none', emailSent = false, next = {}, openTasks = [], markDone = [] }) {
  const interaction = outcome !== 'none' || emailSent;
  const remaining = openTasks.filter(t => !markDone.includes(t.id));
  const date = /^\d{4}-\d{2}-\d{2}$/.test(next.dueOn || '');
  if (!next.mode) return interaction ? 'Elegí la próxima acción o «Sin seguimiento por ahora».' : (markDone.length ? null : 'No hay nada para guardar.');
  if (next.mode === 'new') {
    if (!String(next.title || '').trim()) return 'Escribí la próxima acción.';
    if (!date) return 'Elegí la fecha de la próxima acción.';
    if (remaining.length) return 'Ya hay una próxima acción abierta para este contacto: mantenela, reprogramala o marcala realizada antes de crear otra.';
  }
  if (next.mode === 'reschedule') {
    if (remaining.length !== 1) return 'Para reprogramar tiene que haber una sola próxima acción abierta.';
    if (!date) return 'Elegí la nueva fecha.';
  }
  if (next.mode === 'keep' && !remaining.length) return 'No hay una próxima acción abierta para mantener.';
  if (next.mode === 'update') {
    if (!remaining.some(t => t.id === next.taskId)) return 'La acción que ibas a actualizar ya no está abierta: revisá lo planificado.';
    if (!String(next.title || '').trim()) return 'Escribí la próxima acción.';
    if (!date) return 'Elegí la fecha de la próxima acción.';
  }
  return null;
}

/**
 * The steps one «Guardar» coordinates, in order, without the ones already saved (a retry runs only what is left):
 * activity → contact state → tasks marked done → reschedule → new action.
 */
export function stepsFor({ activity, mark = null, statusTo, markDone = [], reschedule = null, create = null, update = null }, saved = {}) {
  const steps = [];
  if (activity && !saved.activity) steps.push({ id: 'activity' });
  if (mark && !saved.mark) steps.push({ id: 'mark', ...mark });
  if (statusTo && !saved.status) steps.push({ id: 'status', to: statusTo });
  for (const id of markDone) if (!saved[`done:${id}`]) steps.push({ id: `done:${id}`, taskId: id });
  if (reschedule && !saved.reschedule) steps.push({ id: 'reschedule', ...reschedule });
  if (create && !saved.create) steps.push({ id: 'create', ...create });
  if (update && !saved.update) steps.push({ id: 'update', ...update });
  return steps;
}
export const STEP_LABELS = { activity: 'la actividad', mark: 'la marca «Pidió una propuesta» en la respuesta', status: 'el estado del contacto', reschedule: 'la nueva fecha', create: 'la próxima acción', update: 'la próxima acción actualizada' };
export const stepLabel = (id) => (id.startsWith('done:') ? 'la acción marcada como realizada' : STEP_LABELS[id] || id);

/**
 * Runs the steps in order and stops at the first failure: { saved, failed: { id, error } | null }. `saved` grows
 * with every step that worked (with what it returned), so the caller keeps it and a retry skips those.
 */
export async function runSteps(steps, ops, saved = {}) {
  const next = { ...saved };
  for (const s of steps) {
    const op = ops[s.id.startsWith('done:') ? 'done' : s.id];
    try { next[s.id] = (await op(s)) ?? true; }
    catch (e) { return { saved: next, failed: { id: s.id, error: e?.message || String(e) } }; }
  }
  return { saved: next, failed: null };
}

/** What the screen says after a partial failure: what was saved and what is missing. */
export function partialText(saved, failed, steps) {
  const ok = Object.keys(saved).filter(k => saved[k]).map(stepLabel);
  const missing = steps.filter(s => !saved[s.id]).map(s => stepLabel(s.id));
  return `${ok.length ? `Se guardó ${[...new Set(ok)].join(', ')}. ` : 'No se guardó nada. '}Falta ${[...new Set(missing)].join(', ')}${failed?.error ? ` (${failed.error})` : ''}. «Reintentar» guarda solo lo que falta.`;
}

/** The open next actions of one contact (Personal Tasks with Source kind contact), oldest due first. */
export function openContactTasks(day, stakeholderId) {
  if (!day || !stakeholderId) return [];
  const all = [...(day.overdue || []).filter(t => t.type === 'task' || !t.type), ...(day.tasks || []), ...(day.upcoming || []), ...(day.undated || [])];
  const seen = new Set();
  return all.filter(t => t?.status === 'open' && t.source?.kind === 'contact' && t.source.stakeholderId === stakeholderId && !seen.has(t.id) && seen.add(t.id))
    .sort((a, b) => String(a.dueOn || '9999').localeCompare(String(b.dueOn || '9999')));
}

/** The kept record among the ones carrying the same op mark (the oldest), and the extra ones a retry or a double click made. */
export function keepOldest(records = []) {
  const sorted = [...records].sort((a, b) => String(a.createdTime || '').localeCompare(String(b.createdTime || '')) || String(a.id).localeCompare(String(b.id)));
  return { keep: sorted[0] || null, extra: sorted.slice(1) };
}

/**
 * The activity of one «Guardar», exactly once: first read back by its op mark (a retry after a lost answer finds it),
 * create only when nothing carries the mark, then read back again and keep the oldest, removing the extra ones a
 * double click or two simultaneous requests made. Returns the kept record. Airtable has no unique keys: the read
 * after writing is what guarantees one record, not the search before.
 *   find(op) → records with the mark; create(fields) → record; remove(id) → void
 */
export async function ensureActivity({ find, create, remove, op, fields }) {
  if (!validOp(op)) throw new Error('operación inválida');
  const before = keepOldest(await find(op)).keep;
  if (before) return before;
  await create(fields);
  const after = keepOldest(await find(op));
  for (const x of after.extra) { try { await remove(x.id); } catch { /* the other request removed it */ } }
  if (!after.keep) throw new Error('la actividad no se pudo comprobar');
  return after.keep;
}
